import type { CompanyBaselineImportResponse, CompanyResponse, ContractResponse } from '../../api';
import { DataTable, PageHeader, Panel, type Column } from '../../components';
import { useImportCompanyBaseline } from '../queries';
import {
  COMPANY_BASELINE_COPY,
  RESULT_COLUMNS,
  contractLabel,
  resultLabel,
  type MovedOnContract,
  type ResultRow,
} from './baselineImport';
import { SheetBaselineImport, type SheetImportMutation } from './SheetBaselineImport';

/**
 * Loading a whole company's population from the sheet the customer hands over.
 *
 * The per-contract screen's sibling, and shaped by the same three refusals --
 * see [`BaselineImportPage`](./BaselineImportPage.tsx). What is different is
 * that one file now reaches several contracts, which changes two answers:
 *
 *  - **A 409 names contracts, not the contract.** Any one of them having moved
 *    on refuses the whole sheet, so the screen lists which -- by insurer and
 *    codes, the way the sheet itself names them -- with the server's reason for
 *    each, before the override is offered.
 *  - **Success is a table, not a toast.** Each contract got its own import, and
 *    the analyst checking the result wants the rows per sub-contract against
 *    the blocks of the sheet they just sent, so the counts stay on the page.
 *
 * Each contract's history lives on its own import screen, reached from its row
 * on the company page, so it is not repeated here. The form and the four
 * answers are [`SheetBaselineImport`](./SheetBaselineImport.tsx), which the
 * group page's import uses too.
 */

export function CompanyBaselineImportPage({
  company,
  contracts,
  onBack,
}: {
  company: CompanyResponse;
  contracts: readonly ContractResponse[];
  onBack: () => void;
}) {
  const runImport = useImportCompanyBaseline();

  const mutation: SheetImportMutation<CompanyBaselineImportResponse> = {
    error: runImport.error,
    data: runImport.data,
    isPending: runImport.isPending,
    reset: runImport.reset,
    run: (request, onSuccess) =>
      runImport.mutate({ companyId: company.id, ...request }, { onSuccess }),
  };

  const insurerOf = (contractId: string) =>
    contracts.find((contract) => contract.id === contractId);

  const labelOf = (contractId: string, subestipulante: number, modulo: number) =>
    contractLabel(subestipulante, modulo, insurerOf(contractId)?.insurer.name);

  const movedOnColumns: readonly Column<MovedOnContract>[] = [
    {
      key: 'contract',
      header: COMPANY_BASELINE_COPY.conflictContractHeader,
      render: (row) => {
        const contract = insurerOf(row.contractId);
        return contract === undefined
          ? row.contractId
          : labelOf(contract.id, contract.subestipulante, contract.modulo);
      },
    },
    {
      key: 'sentence',
      header: COMPANY_BASELINE_COPY.conflictReasonHeader,
      render: (row) => <q>{row.sentence}</q>,
    },
  ];

  const resultRows = (result: CompanyBaselineImportResponse): readonly ResultRow[] => [
    ...result.contracts.map((item) => ({
      key: item.contract_id,
      label: resultLabel(
        labelOf(item.contract_id, item.subestipulante, item.modulo),
        item.overridden,
      ),
      counts: item,
    })),
    { key: 'total', label: COMPANY_BASELINE_COPY.total, counts: result.totals },
  ];

  return (
    <div className="page">
      <PageHeader
        title={COMPANY_BASELINE_COPY.title}
        description={`${COMPANY_BASELINE_COPY.description} Empresa: ${company.name}.`}
        actions={
          <button type="button" className="button button--ghost" onClick={onBack}>
            {COMPANY_BASELINE_COPY.back}
          </button>
        }
      />

      <SheetBaselineImport
        copy={COMPANY_BASELINE_COPY}
        hasContracts={contracts.length > 0}
        mutation={mutation}
        overrideId="company-baseline-override"
        movedOnColumns={movedOnColumns}
        renderResult={(result) => (
          <Panel
            title={COMPANY_BASELINE_COPY.resultTitle}
            description={COMPANY_BASELINE_COPY.resultDescription}
          >
            <DataTable
              caption={COMPANY_BASELINE_COPY.resultCaption}
              columnVisibility={false}
              columns={RESULT_COLUMNS}
              rows={resultRows(result)}
              rowKey={(row) => row.key}
            />
          </Panel>
        )}
      />
    </div>
  );
}
